import { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { ImageGenerationMode } from '@packages/global';
import { useTranslation } from 'react-i18next';

interface ImageModeSelectorProps {
  value: ImageGenerationMode;
  onChange: (mode: ImageGenerationMode) => void;
  disabled?: boolean;
}

export default function ImageModeSelector({
  value,
  onChange,
  disabled = false
}: ImageModeSelectorProps) {
  const { t } = useTranslation('editor');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const MODE_CONFIG: Record<ImageGenerationMode, { label: string; description: string; emoji: string }> = {
    [ImageGenerationMode.IMAGE_TO_IMAGE]: { label: t('common.imageModeEdit'), description: t('common.imageModeEditDesc'), emoji: '🖼️' },
    [ImageGenerationMode.TEXT_TO_IMAGE]: { label: t('common.imageModeRef'), description: t('common.imageModeRefDesc'), emoji: '📝' }
  };

  const currentMode = MODE_CONFIG[value];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (mode: ImageGenerationMode) => {
    onChange(mode);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm text-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <PhotoIcon className="h-4 w-4 text-orange-400" />
        <span>{currentMode.label}</span>
        <ChevronDownIcon className="h-3 w-3 text-gray-500" />
      </button>

      {isOpen && (
        <div className="absolute bottom-full left-0 mb-1 bg-gray-800 border border-gray-700 rounded-lg shadow-lg overflow-hidden z-20 min-w-[120px]">
          {Object.entries(MODE_CONFIG).map(([mode, config]) => (
            <button
              key={mode}
              onClick={() => handleSelect(mode as ImageGenerationMode)}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-700 transition-colors flex items-center gap-2 ${value === mode ? 'text-blue-400 bg-gray-700/50' : 'text-gray-300'
                }`}
            >
              <span>{config.emoji}</span>
              <span>{config.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
